import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, ShieldCheck } from 'lucide-react';

export default function Footer() {
  return (
    <footer 
      className="bg-slate-950 text-slate-300 border-t border-slate-900"
      itemScope 
      itemType="https://schema.org/LocalBusiness"
    >
      <meta itemProp="name" content="BM Maler A/S" />
      <meta itemProp="legalName" content="BM MALER A/S" />
      <meta itemProp="url" content="https://bmmaler.dk" />
      <meta itemProp="priceRange" content="$$" />
      {/* Upper Footer Vibe */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <Link to="/" className="inline-block" aria-label="BM Maler A/S Forside">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=240,quality=85,format=auto/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/97f3f97c-e329-43a2-8e51-2f2cfda5a57e.png"
                alt="BM Maler Logo"
                width="240"
                height="56"
                loading="lazy"
                className="h-14 w-auto object-contain brightness-0 invert"
              />
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed">
              BM Maler A/S har siden 1957 leveret kompromisløst malerarbejde af højeste kvalitet til både private og erhverv. Vi vægter ordentlighed, faglig stolthed og overholdelse af alle aftaler højt.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://www.facebook.com/bmmaler/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 rounded-lg bg-slate-900 hover:bg-[#F1B528] hover:text-slate-950 transition-all duration-300 text-slate-400"
                aria-label="BM Maler på Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/bmmaler/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 rounded-lg bg-slate-900 hover:bg-[#F1B528] hover:text-slate-950 transition-all duration-300 text-slate-400"
                aria-label="BM Maler på Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold text-base mb-6 tracking-wide uppercase">Hurtige links</h3>
            <ul className="space-y-3.5 text-sm">
              <li>
                <Link to="/profil" className="hover:text-[#F1B528] transition-colors">Om os</Link>
              </li>
              <li>
                <Link to="/galleri" className="hover:text-[#F1B528] transition-colors">Galleri</Link>
              </li>
              <li>
                <Link to="/kundecases" className="hover:text-[#F1B528] transition-colors">Kundecases</Link>
              </li>
              <li>
                <Link to="/laerling-i-bm-maler" className="hover:text-[#F1B528] transition-colors">Lærlinge i BM Maler</Link>
              </li>
              <li>
                <Link to="/super-svende-soeges-til-bm-maler-i-vejle" className="hover:text-[#F1B528] transition-colors">Ledige stillinger</Link>
              </li>
              <li>
                <Link to="/hvad-koster-en-maler" className="hover:text-[#F1B528] transition-colors">Hvad koster en maler</Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-bold text-base mb-6 tracking-wide uppercase">Kontakt os</h3>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#F1B528] shrink-0 mt-0.5" />
                <div>
                  <span className="block text-slate-400 text-xs">Ring direkte</span>
                  <a href="tel:+4575695900" itemProp="telephone" aria-label="Ring til BM Maler på 75 69 59 00" className="text-[#F1B528] hover:text-[#D99C1B] transition-colors font-semibold whitespace-nowrap">75 69 59 00</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#F1B528] shrink-0 mt-0.5" />
                <div>
                  <span className="block text-slate-400 text-xs">Send en e-mail</span>
                  <a href="mailto:bm@bmmaler.dk" itemProp="email" aria-label="Send e-mail til BM Maler på bm@bmmaler.dk" className="text-[#F1B528] hover:text-[#D99C1B] transition-colors font-semibold whitespace-nowrap">bm@bmmaler.dk</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#F1B528] shrink-0 mt-0.5" />
                <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                  <span className="block text-slate-400 text-xs">Vores adresse</span>
                  <span className="font-semibold">
                    <span itemProp="streetAddress">Håndværkervej 3A</span>, <span itemProp="postalCode">7120</span> <span itemProp="addressLocality">Vejle Øst</span>
                  </span>
                  <meta itemProp="addressCountry" content="DK" />
                </div>
              </li>
            </ul>
          </div>

          {/* Trust & Guarantee */}
          <div className="space-y-6">
            <h3 className="text-white font-bold text-base mb-6 tracking-wide uppercase">Garantiordning</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Vi er medlem af Danske Malermestres Garantiordning. Det er din tryghed for et professionelt og fejlfrit malerarbejde.
            </p>
            <div className="flex items-center gap-4 bg-slate-900/50 p-4 rounded-xl border border-slate-900">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=160,quality=85,format=auto/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/69cdfa93-d212-46d0-9d1c-ba4195739c50.png"
                alt="Danske Malermestre Garantiordning Logo"
                width="160"
                height="48"
                loading="lazy"
                className="h-12 w-auto object-contain shrink-0"
              />
              <div className="text-xs">
                <span className="block font-bold text-white">Danske Malermestre</span>
                <span className="text-slate-400">Medlem af garantiordningen</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-900 bg-slate-950 py-8 px-4 sm:px-6 lg:px-8 text-xs sm:text-sm text-slate-500">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            &copy; {new Date().getFullYear()} BM Maler A/S. Alle rettigheder forbeholdes. <span itemProp="vatID">CVR: 25999193</span>
          </div>
          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            <a
              href="https://www.iubenda.com/privacy-policy/35972405"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-[#F1B528] transition-colors"
              title="Privatlivspolitik "
            >
              Privatlivspolitik
            </a>
            <a
              href="https://www.iubenda.com/privacy-policy/35972405/cookie-policy"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-[#F1B528] transition-colors"
              title="Cookiepolitik "
            >
              Cookiepolitik
            </a>
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
              Uddannede malere
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
